import { Component, EventEmitter, OnDestroy, Output } from '@angular/core';
import { ImportBase } from './import.service';
import { ImportField, ImportState, FileParsingError } from './import.states';
import { Store, select } from '@ngrx/store';
import { lastValueFrom, Observable, Subscription, take } from 'rxjs';
import { ImportActions } from './import.actions';
import {
  importedFieldsSelector,
  isLoadedSelector,
  selectedImportedFieldsSelector,
  fileParsingErrorsSelector,
  hasErrorsSelector
} from './import.selector';
import { FieldState } from '../fields/fields.states';
import { FieldActions } from '../fields/fields.actions';
import { Field } from '../fields/field.model';
import { ImportIsoxml } from './importISOXML.service';
import { ImportShapefile } from './importSHAPEFILE.service';

type ImportType = "IsoXML" | "Shapefile";
@Component({
    selector: 'app-field-import',
    templateUrl: './field-import.component.html',
    styleUrls: ['./field-import.component.scss'],
    standalone: false
})
export class FieldImportComponent implements OnDestroy {
  @Output() isDisplayed = new EventEmitter<boolean>();

  files: File[] = [];
  fieldsLoaded$: Observable<boolean>;
  fieldOptions$: Observable<ImportField[]>;
  fileParsingErrors$: Observable<FileParsingError[]>;
  hasErrors$: Observable<boolean>;
  importedFields: ImportField[] = [];

  importFieldSub: Subscription;

  simplifyBorders: boolean = true;

  selectAll: boolean = true; // for select-all toggle button
  hasImportStarted = false;
  isImportFinished = false;
  succesfulImported: number = 0;
  selectedFieldsCount: number = 0;
  currentProgress: number = 0;
  hasError = false;

  defaultImportType: ImportType = 'IsoXML';

  constructor(
    public store: Store<{ imports: ImportState; fields: FieldState }>,
    private isoXMLLoader: ImportIsoxml,
    private shapeFileLoader: ImportShapefile
  ) {
    this.fieldsLoaded$ = this.store
      .select('imports')
      .pipe(select(isLoadedSelector));
    this.fieldOptions$ = this.store
      .select('imports')
      .pipe(select(importedFieldsSelector));
    this.fileParsingErrors$ = this.store
      .select('imports')
      .pipe(select(fileParsingErrorsSelector));
    this.hasErrors$ = this.store
      .select('imports')
      .pipe(select(hasErrorsSelector));

    let defaultHandler: ImportBase;
    if (this.defaultImportType === 'IsoXML') {
        defaultHandler = this.isoXMLLoader;
    } else {
        defaultHandler = this.shapeFileLoader;
    }
    this.store.dispatch(
      ImportActions.selectFileType({ importHandler: defaultHandler })
    );

    this.importFieldSub = this.store
      .select('imports')
      .pipe(select(importedFieldsSelector))
      .subscribe((fields) => {
        this.importedFields = fields;
        this.selectAll =
          fields.length > 0 && fields.every((field) => field.selected);

        this.selectedFieldsCount = fields.filter((field) => field.selected).length;
        this.succesfulImported = 0;
        this.hasError = false;
        fields.forEach((importField) => {
          if (!importField.selected) {
            return;
          }
          if (importField.isImported) {
            this.succesfulImported++;
          }
          if (importField.hasError) {
            this.hasError = true;
          }
          if (
            this.isImportFinished &&
            this.selectedFieldsCount > 0 &&
            this.succesfulImported == this.selectedFieldsCount
          ) {
            setTimeout(() => {
              this.onClose();
            }, 2000);
          }
        });

        this.currentProgress =
          this.selectedFieldsCount > 0
            ? (this.succesfulImported / this.selectedFieldsCount) * 100
            : 0;
      });
  }

  async getCurrentHandler(): Promise<ImportBase> {
    let state = await lastValueFrom(
      this.store.pipe(select('imports'), take(1))
    );
    return state.importHandler;
  }

  async getCurrentFields(): Promise<Field[]> {
    let state = await lastValueFrom(this.store.pipe(select('fields'), take(1)));
    return state.fields;
  }

  onToggleImportHandler(event: any) {
    const importType = event.value as ImportType;
    
    let importHandler: ImportBase;
    if (importType === 'IsoXML') {
        importHandler = this.isoXMLLoader;
    } else {
        importHandler = this.shapeFileLoader;
    }

    this.store.dispatch(
      ImportActions.selectFileType({ importHandler: importHandler })
    );
  }

  removeFile(file: File) {
    this.files = this.files.filter((currentFile) => currentFile !== file);
    this.store.dispatch(
      ImportActions.clearParsingError({ filename: file.name })
    );
  }

  droppedFiles(allFiles: File[]): void {
    this.files = [...this.files, ...allFiles];
  }

  onFileSelect(event: any) {
    const primitiveFileList: FileList = event.target.files;
    this.files = [...this.files, ...Array.from(primitiveFileList)];
  }

  async onUpload() {
    if (this.files.length > 0) {
      this.store.dispatch(ImportActions.clearAllParsingErrors());
      this.store.dispatch(
        ImportActions.loadFiles({
          files: this.files,
          importHandler: await this.getCurrentHandler(),
          existingFields: await this.getCurrentFields(),
          simplify: this.simplifyBorders,
        })
      );
    } else {
      this.onReset();
    }
  }

  onUnselectAll() {
    this.store.dispatch(
      ImportActions.setAllFieldSelections({ selected: !this.selectAll })
    );
  }

  onSimplification() {
    this.simplifyBorders = !this.simplifyBorders;
  }

  isFieldSupposedToBeImported(fieldOption: ImportField) {
    return fieldOption.selected;
  }

  onFieldSelect(fieldToBeImported: ImportField) {
    this.store.dispatch(
      ImportActions.toggleFieldSelection({
        importKey: fieldToBeImported.importKey,
      })
    );
  }

  get collectedFieldErrors(): { fieldName: string; errorMessage: string }[] {
    return this.importedFields
      .filter(
        (importField) => importField.hasError && !!importField.errorMessage?.trim()
      )
      .map((importField) => ({
        fieldName: importField.field.name,
        errorMessage: importField.errorMessage!.trim(),
      }));
  }

  async onImport() {
    const selectedFields = await lastValueFrom(
      this.store
        .select('imports')
        .pipe(select(selectedImportedFieldsSelector), take(1))
    );

    if (selectedFields.length > 0) {
      this.hasImportStarted = true;
      selectedFields.forEach((fieldToBeImported) => {
        this.store.dispatch(
          FieldActions.createField({
            field: fieldToBeImported.field,
            reload: false,
            importKey: fieldToBeImported.importKey,
          })
        );
      });
      this.isImportFinished = true;
    } else {
      this.store.dispatch(
        ImportActions.fileParsingError({ filename: '', errorMessage: 'empty import not possible' })
      );
    }
  }

  onCancel() {
    //TODO: should we really have the same behaviour?
    this.onClose();
  }

  onClose() {
    this.store.dispatch(FieldActions.requestLoad());
    this.store.dispatch(ImportActions.closeImportDialog());
  }

  onReset() {
    // Don't clear the files array - let user manage it manually
    // Just reset the import state
    this.hasImportStarted = false;
    this.isImportFinished = false;
    this.hasError = false;
    this.store.dispatch(ImportActions.resetFile());
  }

  ngOnDestroy(): void {
    this.importFieldSub.unsubscribe();
  }
}
